import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Box,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import FormBuilder from '../../../components/formBuilder/FormBuilder';
import './RateCardConfigForm.scss';
import LocationDropdown from '../../../components/LocationDropdown/LocationDropdown';
import { useDispatch, useSelector } from 'react-redux';
import rateService from '../../../services/rateService';
import { getCar } from '../../../redux/actions/UserCar';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getChooseYourService } from 'redux/actions/UserChooseYourService';
import PackageConfig from './PackageConfig';
import {
  getCompanyDuty,
  getCompanyLocation,
  getRateCard,
  getSubCity,
  clearCompanyDuty,
  clearCompanyLocation,
  clearRateCard,
  clearSubCity,
} from 'redux/actions/UserRateCard';
import { rateCardFiltersEnum, tripDutyTypeEnum } from 'utils/enumObj';
import { getOurCar } from 'redux/actions/UserTopSection';

const getRateTypeByRideType = (rideType) => {
  if (rideType === 'Rental' || rideType === 'Outstation') {
    return 'package';
  }
  return 'flat_per_km';
};

const transformDutyTypeOptions = (dutyTypes) => {
  if (!dutyTypes) return [];
  return dutyTypes.map((duty) => ({
    value: duty.name,
    label: duty.name,
    tripType: duty.tripType,
  }));
};

export default function CityConfigForm({ companyContractId, isConfigureMode = false }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  // Redux selectors
  const carData = useSelector((state) => state?.userCar?.userCar?.data?.body);
  const dutyTypeData = useSelector((state) => state?.userDutyType?.dutyTypeList?.data);
  const cityData = useSelector((state) => state?.userServiceCity?.userServiceCity?.data);

  // Rate card related selectors (reusing from CompanyProfileRateCard)
  const selectedCarData = useSelector((state) => state?.userCar?.userCar?.data);
  const selectedCityData = useSelector(
    (state) => state?.userCompanyLocation?.userCompanyLocation?.data,
  );
  const selectedDutyTypeData = useSelector(
    (state) => state?.userCompanyDuty?.userCompanyDuty?.data,
  );
  const subCityData = useSelector((state) => state?.userSubCity?.userSubCity?.data);

  const companyProfile = useSelector((state) => state?.userCompanyDetails?.companyDetails);

  // Form state
  const [formData, setFormData] = useState({
    locationId: null,
    rideType: '',
    carType: '',
    rateType: 'km',
    baseRate: '',
    surCharge: '',
    platformCharge: '',
    waitingTime: '',
    bufferTime: '',
    extraKmCharge: '',
    graceKm: '',
    extraTimeCharge: '',
    graceTime: '',
    kmCharge: '',
    rateConfig: {
      base_rate_per_km: '',
    },
    peakHours: [],
    isMultiDay: false,
    pricingType: 'perKm',
    flatAmount: '',
    minKm: '',
    nightAllowance: '',
    earlyStartAllowance: '',
  });

  // Component state
  const [rateType, setRateType] = useState(state.rateType || 'km');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rateCardId, setRateCardId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDutyType, setSelectedDutyType] = useState('');
  const [selectedCarType, setSelectedCarType] = useState('');

  // Filter state (from CompanyProfileRateCard)
  const [subCityStatus, setSubCityStatus] = useState(3);
  const [fuelBase, setFuelBase] = useState('102');

  // Prepare options from Redux state
  const cityOptions = isConfigureMode
    ? cityData.map((val) => ({ name: val?.name, id: val?.id }))
    : selectedCityData?.length > 0
      ? selectedCityData.map((val) => ({ name: val?.name, id: val?.id }))
      : [];

  const subCityOptions =
    subCityData?.length > 0 ? subCityData?.map((val) => ({ name: val?.name, id: val?.id })) : [];

  const dutyOptions = isConfigureMode
    ? dutyTypeData.map((val) => ({ id: val.dutyTypeId, name: val.name }))
    : selectedDutyTypeData?.length > 0
      ? selectedDutyTypeData?.map((val) => ({
          id: val.tripType,
          name: val.name,
        }))
      : [];
  const dutyTypeOptions = dutyOptions;

  const carOptions = isConfigureMode
    ? carData?.length > 0
      ? carData.map((val) => ({ id: val?.id, name: val?.carType }))
      : []
    : selectedCarData?.length > 0
      ? selectedCarData?.map((val) => ({ id: val?.id, name: val?.carType }))
      : [];

  const contractId = companyContractId
    ? companyContractId
    : companyProfile?.Contracts?.[0]?.CompanyContracts?.ContractId;

  const [filterValues, setFilterValues] = useState({
    cityType: cityOptions?.length > 0 ? cityOptions?.[0] : '',
    dutyType: '',
    carType: '',
    subCityType: { id: '', name: '' },
    isMultiDay: false,
  });

  useEffect(() => {
    if (companyProfile?.Contracts?.length) {
      dispatch(getCompanyLocation(contractId));
    }
  }, [companyProfile]);

  useEffect(() => {
    if (!filterValues?.dutyType?.id && cityOptions?.[0]?.id) {
      setFilterValues({ ...filterValues, cityType: cityOptions?.[0] });
      setSelectedLocation(cityOptions?.[0]?.id);
      dispatch(
        getCompanyDuty({
          contractId: contractId,
          location: filterValues?.cityType?.id || cityOptions?.[0]?.id,
        }),
      );
    }
  }, [cityOptions?.[0]?.id]);

  useEffect(() => {
    // A4 fix (2026-09-15): this block sends requests built from THREE values
    // (trip type, selected city, contract id) but used to fire as soon as the
    // trip-type list existed. A leftover trip-type list in Redux (no cleanup
    // on unmount) let it run while the profile/city fetches were still in
    // flight, producing cityId=undefined / contractId=undefined — and the
    // sub-location 401 then tripped the global logout interceptor. Fire only
    // when every value it uses is loaded; the dep list re-runs it as each
    // input arrives. (Configure mode never dispatches getCar, so contractId
    // is not required there.)
    const prereqsReady =
      dutyOptions?.[0]?.id && cityOptions?.[0]?.id && (isConfigureMode || contractId);
    if (!filterValues?.dutyType?.id && prereqsReady) {
      setFilterValues({
        ...filterValues,
        dutyType: dutyOptions?.[0],
        isMultiDay: false,
      });
      setSelectedDutyType(dutyOptions?.[0]?.id);

      const dutyTypeName = dutyOptions?.[0]?.name;

      // let newRateType =
      //   dutyTypeName.startsWith("Ride") || dutyTypeName.startsWith("Airport")
      //     ? "km"
      //     : "package";
      let newRateType;
      if (dutyTypeName.startsWith('Airport')) {
        newRateType = 'perKm';
      } else if (dutyTypeName.startsWith('Ride')) {
        newRateType = 'km';
      } else {
        newRateType = 'package';
      }
      setRateType(newRateType);

      setRateType(newRateType);
      setFormData((prev) => ({
        ...prev,
        rideType: dutyTypeName || '',
        rateType: newRateType,
        isMultiDay: false,
      }));

      if (dutyOptions?.[0]?.id === 4) {
        dispatch(getSubCity(cityOptions?.[0]?.id));
        setSubCityStatus(0);
      }

      if (!isConfigureMode) {
        dispatch(
          getCar({
            contractId: contractId,
            type: dutyOptions?.[0]?.id,
            location: cityOptions?.[0]?.id,
          }),
        );
      }
    }
  }, [dutyOptions?.[0]?.id, cityOptions?.[0]?.id, contractId]);

  useEffect(() => {
    if (carOptions?.[0]?.id) {
      setFilterValues({ ...filterValues, carType: carOptions?.[0] });
      setSelectedCarType(carOptions?.[0]?.id);
    }
  }, [carOptions?.[0]?.id]);

  useEffect(() => {
    // Fetch initial rate card configuration when component mounts
    if (selectedLocation && selectedDutyType && selectedCarType && contractId) {
      fetchRateCardConfig();
    }
  }, [selectedLocation, selectedDutyType, selectedCarType, contractId, filterValues?.isMultiDay]);

  const handleChange = (newValue, name) => {
    const filterValuesData = filterValues;
    filterValuesData[name] = newValue;
    setFilterValues(filterValues);
    if (name === rateCardFiltersEnum?.cityType) {
      if (!isConfigureMode) {
        dispatch(
          getCompanyDuty(
            {
              contractId: contractId,
              location: newValue?.id,
            },
            'rateCard',
            setFilterValues,
            filterValuesData,
            setSubCityStatus,
          ),
        );
        dispatch(clearSubCity());

        if (filterValues?.dutyType?.id === 4) {
          dispatch(getSubCity(newValue?.id, 'rateCard', setFilterValues, filterValuesData));
        }
      } else {
        dispatch(getOurCar({ location: cityOptions?.[0]?.id }));
      }
      setFilterValues({ ...filterValues, subCityType: { id: '', name: '' } });
      setSelectedLocation(newValue?.id);
    } else if (name === rateCardFiltersEnum?.dutyType) {
      setSubCityStatus(newValue?.id === 4 ? 0 : 3);

      const dutyTypeName = dutyOptions.find((option) => option.id === newValue?.id)?.name;
      // let newRateType =
      //   dutyTypeName.startsWith("Ride") || dutyTypeName.startsWith("Airport")
      //     ? "km"
      //     : "package";
      let newRateType;
      if (dutyTypeName.startsWith('Airport')) {
        newRateType = 'perKm';
      } else if (dutyTypeName.startsWith('Ride')) {
        newRateType = 'km';
      } else {
        newRateType = 'package';
      }
      setRateType(newRateType);

      setRateType(newRateType);
      setFormData((prev) => ({
        ...prev,
        rideType: dutyTypeName || '',
        rateType: newRateType,
        isMultiDay: false,
      }));

      setSelectedDutyType(newValue?.id);
      setFilterValues({ ...filterValues, [name]: newValue, isMultiDay: false });

      if (!isConfigureMode) {
        dispatch(
          getCar({
            contractId: contractId,
            type: newValue?.id,
            location: filterValues?.cityType?.id || cityOptions?.[0]?.id,
          }),
        );
      }
    } else if (name === rateCardFiltersEnum?.carType) {
      const data = {
        carType: newValue?.id,
        type: filterValuesData?.dutyType?.id,
        baseFuelPrice: parseFloat(fuelBase),
        location: filterValuesData?.cityType?.id,
        contractId: contractId,
        subCity: filterValuesData?.subCityType?.id || '',
        isMultiDay: filterValues?.isMultiDay,
      };
      setSelectedCarType(newValue?.id);
    }
  };

  const handleIsMultiDayToggle = (event) => {
    const newValue = event.target.checked;
    setFilterValues({ ...filterValues, isMultiDay: newValue });
    setFormData((prev) => ({
      ...prev,
      isMultiDay: newValue,
    }));
  };

  async function fetchRateCardConfig() {
    const currentFilters = {
      location: selectedLocation,
      rideType: dutyOptions.find((option) => option.id === selectedDutyType)?.name,
      carType: selectedCarType,
      contractId,
      isMultiDay: filterValues?.isMultiDay,
    };
    // Call getRateCard if at least one filter has a value
    if (currentFilters.location || currentFilters.rideType || currentFilters.carType) {
      setIsLoading(true);
      try {
        const currentRateType = getRateTypeByRideType(currentFilters.rideType);
        const result = await rateService.getRateCard(
          currentFilters.location || '',
          currentFilters.rideType || '',
          currentFilters.carType || '',
          currentFilters.contractId || '',
          dutyTypeOptions,
          currentRateType,
          currentFilters.isMultiDay || false,
        );

        if (result.success) {
          setHasExistingConfig(result.data.hasExistingConfig);

          if (result.data.hasExistingConfig && result.data.formattedData) {
            // Update form with existing configuration
            const isAirportFetch = currentFilters.rideType?.startsWith('Airport');
            setFormData({
              ...result.data.formattedData,
              rateType: isAirportFetch
                ? result.data.formattedData.pricingType || 'perKm'
                : result.data.formattedData.rateType,
            });
            setRateCardId(result.data.data[0].id);
          } else {
            // Reset form but keep current filter values
            setFormData((prev) => ({
              ...prev,
              locationId: currentFilters.location || prev.locationId,
              rideType: currentFilters.rideType || prev.rideType,
              carType: currentFilters.carType || prev.carType,
              isMultiDay: currentFilters.isMultiDay || false,
              // Use the current ride type to determine rate type if it's a ride type change
              rateType: currentFilters.rideType.startsWith('Airport')
                ? 'perKm' // Airport ke liye default perKm
                : currentFilters.rideType.startsWith('Ride')
                  ? 'km'
                  : 'package',
              baseRate: '',
              surCharge: '',
              platformCharge: '',
              waitingTime: '',
              bufferTime: '',
              extraKmCharge: '',
              graceKm: '',
              extraTimeCharge: '',
              graceTime: '',
              kmCharge: '',
              rateConfig: {
                base_rate_per_km: '',
              },
              pricingType: 'perKm',
              flatAmount: '',
              minKm: '',
            }));
            setRateCardId(null);
          }
          setHasChanges(false);
        }
      } catch (error) {
        console.error('Error fetching rate card:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Reset form data if no filters are selected
      setFormData((prev) => ({
        ...prev,
        locationId: '',
        rideType: '',
        carType: '',
        rateType: '',
        baseRate: '',
        surCharge: '',
        platformCharge: '',
        waitingTime: '',
        bufferTime: '',
        extraKmCharge: '',
        graceKm: '',
        extraTimeCharge: '',
        graceTime: '',
        kmCharge: '',
        rateConfig: {
          base_rate_per_km: '',
        },
        isMultiDay: false,
      }));
      setHasExistingConfig(false);
      setRateCardId(null);
    }
  }

  // Handle form data changes
  const handleFormChange = useCallback((updatedData) => {
    setFormData((prev) => {
      const hasActualChanges = Object.keys(updatedData).some((key) => {
        if (key === 'packages') {
          const prevPackages = prev[key] || [];
          const updatedPackages = updatedData[key] || [];
          return JSON.stringify(prevPackages) !== JSON.stringify(updatedPackages);
        }
        return String(updatedData[key]) !== String(prev[key]);
      });

      if (hasActualChanges) {
        setHasChanges(true);
      }

      return { ...prev, ...updatedData };
    });
  }, []);

  // Get rate type options based on selected ride type
  // const getRateTypeOptions = useCallback(() => {
  //   const selectedRideType = filterValues.dutyType?.name || formData.rideType;

  //   if (
  //     selectedRideType?.startsWith("Ride") ||
  //     selectedRideType?.startsWith("Airport")
  //   ) {
  //     return [{ value: "km", label: "/km", disabled: false }];
  //   }

  //   if (selectedRideType === "Rental" || selectedRideType === "Outstation") {
  //     return [{ value: "package", label: "Package", disabled: false }];
  //   }

  //   return [
  //     { value: "km", label: "/km", disabled: true },
  //     { value: "package", label: "Package", disabled: true },
  //   ];
  // }, [filterValues.dutyType?.name, formData.rideType]);

  const getRateTypeOptions = useCallback(() => {
    const selectedRideType = filterValues.dutyType?.name || formData.rideType;

    if (selectedRideType?.startsWith('Airport')) {
      return [
        { value: 'perKm', label: '/km', disabled: false },
        { value: 'flatRate', label: 'Flat Rate', disabled: false },
        { value: 'flatPlusKm', label: 'Flat Rate + /km', disabled: false },
      ];
    }

    if (selectedRideType?.startsWith('Ride')) {
      return [{ value: 'km', label: '/km', disabled: false }];
    }

    if (selectedRideType === 'Rental' || selectedRideType === 'Outstation') {
      return [{ value: 'package', label: 'Package', disabled: false }];
    }

    return [
      { value: 'km', label: '/km', disabled: true },
      { value: 'package', label: 'Package', disabled: true },
    ];
  }, [filterValues.dutyType?.name, formData.rideType]);

  // Form configuration
  const getFormConfig = () => {
    const selectedRideType = filterValues.dutyType?.name || formData.rideType;
    const isAirport = selectedRideType?.startsWith('Airport');

    const formFields = [
      {
        name: 'rateType',
        inputType: 'select',
        options: getRateTypeOptions(),
        value: formData.rateType,
        validation: { required: 'Rate Type is required' },
      },
    ];

    if (isAirport) {
      // ---- /km (perKm) ----
      formFields.push({
        name: 'baseRate',
        label: 'Base Rate',
        inputType: 'number',
        prefix: '₹',
        validation: {
          required: formData.rateType === 'perKm' ? 'Base Rate is required' : false,
          min: { value: 1, message: 'Must be greater than 0' },
        },
        visible: formData.rateType === 'perKm',
      });

      // ---- Flat Rate + Flat Rate + /km (dono me common) ----
      formFields.push({
        name: 'flatAmount',
        label: 'Flat Rate',
        inputType: 'number',
        prefix: '₹',
        validation: {
          required:
            formData.rateType === 'flatRate' || formData.rateType === 'flatPlusKm'
              ? 'Flat Rate is required'
              : false,
          min: { value: 1, message: 'Must be greater than 0' },
        },
        visible: formData.rateType === 'flatRate' || formData.rateType === 'flatPlusKm',
      });

      // ---- sirf Flat Rate + /km me ----
      formFields.push({
        name: 'minKm',
        label: 'Minimum KM',
        inputType: 'number',
        validation: {
          required: formData.rateType === 'flatPlusKm' ? 'Minimum KM is required' : false,
          min: { value: 1, message: 'Must be greater than 0' },
        },
        visible: formData.rateType === 'flatPlusKm',
      });
    } else {
      // ---- non-Airport, purana behaviour as-is ----
      formFields.push({
        name: 'baseRate',
        label: 'Base Rate',
        inputType: 'number',
        prefix: '₹',
        validation: {
          required: 'Base Rate is required',
          min: { value: 1, message: 'Must be greater than 0' },
        },
      });
    }

    // ---- Surcharge, Platform Charge — sabme common ----
    formFields.push(
      {
        name: 'surCharge',
        label: 'Surcharge',
        inputType: 'number',
        prefix: '₹',
        validation: {
          min: { value: 1, message: 'Must be greater than 0' },
        },
      },
      {
        name: 'platformCharge',
        label: 'Platform Charge',
        inputType: 'number',
        prefix: '₹',
        validation: {
          min: { value: 1, message: 'Must be greater than 0' },
        },
      },
    );

    // ---- /km ka rate — sirf perKm aur flatPlusKm me ----
    if (isAirport) {
      formFields.push({
        name: 'currentRate',
        label: '/KM',
        inputType: 'number',
        prefix: '₹',
        validation: {
          required:
            formData.rateType === 'perKm' || formData.rateType === 'flatPlusKm'
              ? '/KM rate is required'
              : false,
          min: { value: 1, message: 'Must be greater than 0' },
        },
        visible: formData.rateType === 'perKm' || formData.rateType === 'flatPlusKm',
      });

      formFields.push({
        name: 'nightAllowance',
        label: 'Night Allowance',
        inputType: 'number',
        prefix: '₹',
      });

      formFields.push({
        name: 'earlyStartAllowance',
        label: 'Early Start Allowance',
        inputType: 'number',
        prefix: '₹',
      });
    }

    const visibleFormFields = formFields.filter((field) => field.visible !== false);

    return {
      title: 'Configure Rates',
      formField: [
        {
          title: 'Basic Configuration',
          formFields: visibleFormFields,
        },
      ],
      selectedRideType: filterValues.dutyType,
      rateType,
    };
  };

  // Save form data
  const handleSaveFormData = useCallback(async () => {
    const locationToUse = formData.locationId || filterValues.cityType?.id;
    if (!locationToUse) {
      toast.warning('Please select a location');
      return;
    }
    setIsSaving(true);
    try {
      const result = await rateService.createRateCard(
        formData,
        locationToUse,
        contractId,
        dutyTypeOptions,
      );
      const newRateCardId = result?.data?.data?.created?.[0]?.id || result?.data?.created?.[0]?.id;

      if (result.success) {
        setHasChanges(false);
        setHasExistingConfig(true);
        setRateCardId(newRateCardId);
        toast.success('Rate card saved successfully');
      }
    } catch (error) {
      console.error('Error saving rate card:', error);
      toast.error('Error saving rate card');
    } finally {
      setIsSaving(false);
    }
  }, [formData, filterValues.cityType?.id, dutyTypeOptions, contractId]);

  // Update form data
  const handleUpdateFormData = useCallback(async () => {
    if (!rateCardId) return;
    const isAirport = (filterValues.dutyType?.name || formData.rideType)?.startsWith('Airport');
    setIsUpdating(true);
    try {
      const result = await rateService.updateRateCard(rateCardId, formData, isAirport);
      if (result.success) {
        setHasChanges(false);
        toast.success('Rate card updated successfully');
      }
    } catch (error) {
      console.error('Error updating rate card:', error);
      toast.error('Error updating rate card');
    } finally {
      setIsUpdating(false);
    }
  }, [rateCardId, formData, filterValues.dutyType?.name]);

  const hasInvalidValues = () => {
    const selectedRideType = filterValues.dutyType?.name || formData.rideType;
    const isAirport = selectedRideType?.startsWith('Airport');

    let requiredFields = ['baseRate'];

    if (isAirport) {
      if (formData.rateType === 'perKm') {
        requiredFields = ['baseRate', 'currentRate'];
      } else if (formData.rateType === 'flatRate') {
        requiredFields = ['flatAmount'];
      } else if (formData.rateType === 'flatPlusKm') {
        requiredFields = ['flatAmount', 'minKm', 'currentRate'];
      }
    } else if (formData.rateType === 'km') {
      requiredFields.push('kmCharge');
    }

    return requiredFields.some((field) => {
      const value = parseFloat(formData[field]);
      return isNaN(value) || value <= 0;
    });
  };

  const selectSx = {
    height: '40px',
    '& .MuiSelect-select': {
      padding: '8px 14px',
    },
  };

  return (
    <>
      <div className="city-config-form">
        <div
          style={{
            display: 'flex',
            gap: '20px',
            marginBottom: '20px',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}
        >
          <Select
            style={{ flex: 0.3, minWidth: '150px' }}
            size="small"
            sx={selectSx}
            value={selectedLocation ?? ''}
            onChange={(e) => handleChange({ id: e.target.value }, 'cityType')}
          >
            {cityOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>

          <Select
            style={{ flex: 0.3, minWidth: '150px' }}
            size="small"
            sx={selectSx}
            value={selectedDutyType ?? ''}
            onChange={(e) => handleChange({ id: e.target.value }, 'dutyType')}
          >
            {dutyOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>

          <Select
            style={{ flex: 0.3, minWidth: '150px' }}
            size="small"
            sx={selectSx}
            value={selectedCarType ?? ''}
            onChange={(e) => handleChange({ id: e.target.value }, 'carType')}
          >
            {carOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>

          {filterValues?.dutyType?.id === tripDutyTypeEnum.rental && (
            <FormControlLabel
              control={
                <Switch
                  checked={filterValues?.isMultiDay}
                  onChange={handleIsMultiDayToggle}
                  disabled={false}
                />
              }
              label="Multi Day"
              style={{ minWidth: '150px' }}
            />
          )}
        </div>

        {isLoading ? (
          <div>Loading rate card data...</div>
        ) : (
          <>
            {formData.rateType === 'package' ? (
              <PackageConfig
                initialData={formData}
                onChange={handleFormChange}
                selectedLocation={selectedLocation}
                selectedRideType={filterValues.dutyType?.name || formData.rideType}
                selectedCarType={selectedCarType}
                contractId={contractId}
                rateCardId={rateCardId}
                isUpdate={hasExistingConfig}
                isConfigureMode={isConfigureMode}
                isMultiDay={filterValues?.isMultiDay}
              />
            ) : (
              <>
                <FormBuilder
                  formConfig={getFormConfig()}
                  values={formData}
                  onChange={handleFormChange}
                  hideSubmitButton={true}
                />

                {isConfigureMode && (
                  <div className="save-button-container" style={{ display: 'flex', gap: '10px' }}>
                    {hasExistingConfig ? (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpdateFormData}
                        disabled={isLoading || isUpdating || !hasChanges || hasInvalidValues()}
                        sx={{ textTransform: 'none' }}
                      >
                        {isUpdating ? 'Updating...' : 'Update Configuration'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveFormData}
                        disabled={isLoading || isSaving || !hasChanges || hasInvalidValues()}
                        sx={{ textTransform: 'none' }}
                      >
                        {isSaving ? 'Saving...' : 'Save Configuration'}
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </>
  );
}
